// ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { notification } from 'antd';
import config from '../config';
import apiService from '../utils/apiInterceptor';
import passwordService from '../services/passwordService';
import PasswordStrengthMeter from './PasswordStrengthMeter';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const navigate = useNavigate();
  
  // Extract token and email from URL parameters or query string
  const { token } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');
  const resetToken = token || queryParams.get('token');

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!resetToken || !email) {
        setIsTokenValid(false);
        notification.error({
          message: 'Invalid Reset Link',
          description: 'The password reset link is invalid or has expired.',
        });
        return;
      }

      try {
        // Verify token validity with the backend
        const response = await passwordService.verifyResetToken(resetToken, email);
        
        if (!response.valid) {
          setIsTokenValid(false);
          notification.error({
            message: 'Invalid Reset Link',
            description: 'The password reset link is invalid or has expired.',
          });
        }
      } catch (err) {
        // If the API call fails, we still allow the user to try resetting
        // The actual reset attempt will validate the token again
        console.error('Error validating token:', err);
      }
    };

    validateToken();
  }, [resetToken, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!password || !confirmPassword) {
      notification.error({
        message: 'Fields Required',
        description: 'Please enter and confirm your new password.',
      });
      return;
    }
    
    if (password !== confirmPassword) {
      notification.error({
        message: 'Passwords Do Not Match',
        description: 'The passwords you entered do not match.',
      });
      return;
    }
    
    if (password.length < 8) {
      notification.error({
        message: 'Password Too Short',
        description: 'Your password must be at least 8 characters long.',
      });
      return;
    }
    
    setIsLoading(true);

    try {
      // Call the password reset API
      const response = await passwordService.resetPassword(
        email,
        resetToken,
        password,
        confirmPassword
      );
      
      if (response.success) {
        setIsSuccess(true);
        notification.success({
          message: 'Password Reset Successfully',
          description: 'Your password has been reset. You can now login with your new password.',
        });
        
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to reset password');
      }
    } catch (err) {
      notification.error({
        message: 'Reset Failed',
        description: err.message || 'There was an error resetting your password. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show error if token is invalid
  if (!isTokenValid) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-golden-navy">
        <div className="w-full max-w-md p-8 space-y-8 bg-golden-navy text-white">
          <div className="text-center">
            <img src="/ico/gglogolarge.svg" alt="Golden Group Logo" className="mx-auto w-64" />
            <h2 className="mt-6 text-3xl font-bold">Invalid Reset Link</h2>
            <p className="mt-2">
              The password reset link is invalid or has expired. Please request a new one.
            </p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full flex justify-center py-2 px-4 border border-golden-yellow rounded-md shadow-sm text-sm font-medium text-white bg-golden-yellow hover:bg-golden-yellow-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
            >
              Request New Reset Link
            </button>
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate('/login')}
              className="w-full flex justify-center py-2 px-4 border border-golden-yellow rounded-md shadow-sm text-sm font-medium text-white bg-transparent hover:bg-golden-yellow/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
            >
              Back To Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (isSuccess) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-golden-navy">
        <div className="w-full max-w-md p-8 space-y-8 bg-golden-navy text-white">
          <div className="text-center">
            <img src="/ico/gglogolarge.svg" alt="Golden Group Logo" className="mx-auto w-64" />
            <h2 className="mt-6 text-3xl font-bold">Password Reset Successful</h2>
            <p className="mt-2">
              Your password has been reset successfully. You will be redirected to the login page.
            </p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => navigate('/login')}
              className="w-full flex justify-center py-2 px-4 border border-golden-yellow rounded-md shadow-sm text-sm font-medium text-white bg-golden-yellow hover:bg-golden-yellow-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show reset password form
  return (
    <div className="flex items-center justify-center min-h-screen bg-golden-navy">
      <div className="w-full max-w-md p-8 space-y-8 bg-golden-navy text-white">
        <div className="text-center">
          <img src="/ico/gglogolarge.svg" alt="Golden Group Logo" className="mx-auto w-64" />
          <h2 className="mt-6 text-3xl font-bold">Reset Your Password</h2>
          <p className="mt-2">Enter your new password below.</p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="password" className="block text-sm font-medium">
              New Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              className="mt-1 block w-full px-3 py-2 bg-white border border-golden-yellow rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <PasswordStrengthMeter password={password} />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              className="mt-1 block w-full px-3 py-2 bg-white border border-golden-yellow rounded-md text-golden-navy placeholder-gray-400 focus:outline-none focus:ring-golden-yellow focus:border-golden-yellow"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <div>
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-golden-navy bg-white hover:bg-golden-yellow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>

          <div>
            <button
              type="button"
              className="w-full flex justify-center py-2 px-4 border border-golden-yellow rounded-md shadow-sm text-sm font-medium text-white bg-transparent hover:bg-golden-yellow/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-golden-yellow"
              onClick={() => navigate('/login')}
            >
              Back To Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;